import { get, send, unwrap } from "./api.js";
import { CARD_RECORD_PAGE_SIZE } from "./paginationPageSizes.js";

export type HubAssetProvider = "groove";

export type RepertoireStatus =
  | "learning"
  | "polishing"
  | "performance_ready"
  | "completed"
  | "paused";

export const REPERTOIRE_STATUS_LABELS: Record<RepertoireStatus, string> = {
  learning: "Learning",
  polishing: "Polishing",
  performance_ready: "Performance-ready",
  completed: "Completed",
  paused: "Paused",
};

export interface RepertoireEntry {
  id: string;
  studentId: string;
  libraryPieceId?: string | null;
  title: string;
  composer: string | null;
  workOrCollection: string | null;
  instrument: string | null;
  status: RepertoireStatus;
  startedOn: string | null;
  completedOn: string | null;
  createdByName?: string | null;
  tutorNote?: string | null;
  familyVisible?: boolean;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RepertoireLibraryPiece {
  id: string;
  title: string;
  composer: string | null;
  workOrCollection: string | null;
  instrument: string | null;
  genre: string | null;
  difficulty: string | null;
  tempoBpm?: number | null;
  visibility?: "shared" | "private";
  createdByUserId?: string | null;
  createdByName?: string | null;
  canChangeVisibility?: boolean;
  tags: string[];
  canArchive?: boolean;
  archivedAt?: string | null;
  assetCount?: number;
  studentCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface GrooveRepertoireMaterialSnapshot {
  provider: "groove";
  title: string;
  query: string;
  practiceInstructions: [];
}

export interface RepertoireMaterial {
  id: string;
  title: string;
  description: string | null;
  kind: "file" | "link" | "hub_asset";
  originalFilename: string | null;
  externalUrl: string | null;
  hubProvider: string | null;
  hubAssetId: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  presentationKind: "pdf" | "image" | "audio" | "video" | "file" | "link" | "hub_asset";
  shared?: boolean;
  canRemove?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RepertoireCounts {
  learning: number;
  polishing: number;
  performance_ready: number;
  completed: number;
  paused: number;
}

export interface RepertoirePage {
  items: RepertoireEntry[];
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

export interface RepertoireList {
  counts: RepertoireCounts;
  current: RepertoireEntry[];
  completed: RepertoirePage;
  archived?: RepertoirePage;
}

export interface RepertoireInput {
  libraryPieceId?: string | null;
  title: string;
  composer?: string | null;
  workOrCollection?: string | null;
  instrument?: string | null;
  status?: RepertoireStatus;
  startedOn?: string | null;
  completedOn?: string | null;
  tutorNote?: string | null;
  familyVisible?: boolean;
}

export interface LessonRepertoireSelection {
  repertoireId: string;
  resourceIds: string[];
  expectedSharedResourceIds: string[];
  practice: boolean;
  songTask: boolean;
}
export interface LessonRepertoireSelectionResult { title: string; resourceIds: string[]; taskIds: string[]; withdrawnTaskIds?: string[]; familyVisible?: boolean }
export function assignLessonRepertoire(lessonId: string, selection: LessonRepertoireSelection, requestId: string): Promise<LessonRepertoireSelectionResult> {
  const { repertoireId, ...input } = selection;
  return send("POST", `/admin/lessons/${encodeURIComponent(lessonId)}/repertoire/${encodeURIComponent(repertoireId)}/assign`, { ...input, requestId });
}

function basePath(prefix: "/admin" | "/me", studentId: string): string {
  return `${prefix}/students/${encodeURIComponent(studentId)}/repertoire`;
}

function listPath(prefix: "/admin" | "/me", studentId: string, completedPage = 1, archivedPage = 1): string {
  const params = new URLSearchParams({ completedPageSize: String(CARD_RECORD_PAGE_SIZE) });
  if (completedPage > 1) params.set("completedPage", String(completedPage));
  if (archivedPage > 1) params.set("archivedPage", String(archivedPage));
  return `${basePath(prefix, studentId)}?${params}`;
}

export async function getStaffRepertoire(studentId: string, completedPage = 1, archivedPage = 1): Promise<RepertoireList> {
  return get<RepertoireList>(listPath("/admin", studentId, completedPage, archivedPage));
}

export async function getFamilyRepertoire(studentId: string, completedPage = 1, archivedPage = 1): Promise<RepertoireList> {
  return get<RepertoireList>(listPath("/me", studentId, completedPage, archivedPage));
}

export async function addRepertoireEntry(
  studentId: string,
  input: RepertoireInput,
): Promise<RepertoireEntry> {
  const response = await send<{ entry: RepertoireEntry }>(
    "POST",
    basePath("/admin", studentId),
    input,
  );
  return response.entry;
}

export async function updateRepertoireEntry(
  studentId: string,
  repertoireId: string,
  input: Partial<RepertoireInput> & { archived?: boolean },
): Promise<RepertoireEntry> {
  const response = await send<{ entry: RepertoireEntry }>(
    "PATCH",
    `${basePath("/admin", studentId)}/${encodeURIComponent(repertoireId)}`,
    input,
  );
  return response.entry;
}


export async function searchRepertoireLibrary(query = ""): Promise<RepertoireLibraryPiece[]> {
  const result = await get<{ items: RepertoireLibraryPiece[] }>("/admin/repertoire-library/search", {
    q: query,
  });
  return result.items;
}

export interface RepertoireCatalogueQuery {
  q?: string;
  view?: "active" | "private" | "archived";
  genre?: string;
  instrument?: string;
  difficulty?: string;
  createdBy?: string;
  minTempo?: string;
  maxTempo?: string;
  sort?: "title" | "tempo" | "genre" | "creator" | "updated" | "materials";
  offset?: number;
}

export interface RepertoireCatalogue {
  items: RepertoireLibraryPiece[];
  total: number;
  offset: number;
  limit: number;
  filters: { genres: string[]; instruments: string[]; difficulties: string[]; creators: { id: string; name: string }[] };
}

export function getRepertoireCatalogue(input: RepertoireCatalogueQuery): Promise<RepertoireCatalogue> {
  return get("/admin/repertoire-library/catalogue", Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined && value !== "").map(([key, value]) => [key, String(value)]),
  ));
}

export interface RepertoireLibraryInput {
  title: string;
  composer?: string | null;
  workOrCollection?: string | null;
  instrument?: string | null;
  genre?: string | null;
  difficulty?: string | null;
  tempoBpm?: number | null;
  visibility?: "shared" | "private";
  tags?: string[];
}

export async function createRepertoireLibraryPiece(input: RepertoireLibraryInput): Promise<RepertoireLibraryPiece> {
  const result = await send<{ piece: RepertoireLibraryPiece }>(
    "POST",
    "/admin/repertoire-library",
    input,
  );
  return result.piece;
}

export async function updateRepertoireLibraryPiece(
  pieceId: string,
  input: Partial<RepertoireLibraryInput> & { archived?: boolean },
): Promise<RepertoireLibraryPiece> {
  const result = await send<{ piece: RepertoireLibraryPiece }>(
    "PATCH",
    `/admin/repertoire-library/${encodeURIComponent(pieceId)}`,
    input,
  );
  return result.piece;
}

export async function getRepertoireLibraryPiece(pieceId: string): Promise<{
  piece: RepertoireLibraryPiece;
  resources: RepertoireMaterial[];
  studentCount: number;
}> {
  return get(`/admin/repertoire-library/${encodeURIComponent(pieceId)}`);
}

export async function addRepertoireLibraryLink(
  pieceId: string,
  input: { title: string; description?: string | null; url: string },
): Promise<RepertoireMaterial> {
  const result = await send<{ resource: RepertoireMaterial }>(
    "POST",
    `/admin/repertoire-library/${encodeURIComponent(pieceId)}/resources`,
    input,
  );
  return result.resource;
}


export async function addRepertoireLibraryFile(
  pieceId: string,
  input: { file: File; title?: string; description?: string | null },
): Promise<RepertoireMaterial> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.title?.trim()) form.append("title", input.title.trim());
  if (input.description?.trim()) form.append("description", input.description.trim());
  const result = await unwrap<{ resource: RepertoireMaterial }>(
    await fetch(
      `/api/admin/repertoire-library/${encodeURIComponent(pieceId)}/resources/files`,
      {
        method: "POST",
        headers: { Accept: "application/json" },
        body: form,
      },
    ),
  );
  return result.resource;
}

export function staffRepertoireLibraryFileOpenPath(pieceId: string, resourceId: string): string {
  return `/api/admin/repertoire-library/${encodeURIComponent(pieceId)}/resources/${encodeURIComponent(resourceId)}/open`;
}

export async function getRepertoireLibraryHubMaterial(pieceId: string, resourceId: string): Promise<GrooveRepertoireMaterialSnapshot> {
  return get(`/admin/repertoire-library/${encodeURIComponent(pieceId)}/resources/${encodeURIComponent(resourceId)}/hub`);
}

export async function getStaffRepertoireMaterials(
  studentId: string,
  repertoireId: string,
): Promise<RepertoireMaterial[]> {
  const result = await get<{ items: RepertoireMaterial[] }>(
    `/admin/students/${encodeURIComponent(studentId)}/repertoire/${encodeURIComponent(repertoireId)}/materials`,
  );
  return result.items;
}

export async function getFamilyRepertoireMaterials(
  studentId: string,
  repertoireId: string,
): Promise<RepertoireMaterial[]> {
  const result = await get<{ items: RepertoireMaterial[] }>(
    `/me/students/${encodeURIComponent(studentId)}/repertoire/${encodeURIComponent(repertoireId.replace(/^private:/, ""))}/materials`,
  );
  return result.items;
}

export function setRepertoireMaterialShared(
  studentId: string,
  repertoireId: string,
  resourceId: string,
  shared: boolean,
): Promise<void> {
  const target =
    `/admin/students/${encodeURIComponent(studentId)}/repertoire/${encodeURIComponent(repertoireId)}/materials/${encodeURIComponent(resourceId)}/share`;
  return send(shared ? "PUT" : "DELETE", target);
}

export function familyRepertoireMaterialOpenPath(
  studentId: string,
  repertoireId: string,
  resourceId: string,
): string {
  return `/api/me/students/${encodeURIComponent(studentId)}/repertoire/${encodeURIComponent(repertoireId.replace(/^private:/, ""))}/materials/${encodeURIComponent(resourceId)}/open`;
}


export async function getHubAssetRepertoireLinks(
  provider: HubAssetProvider,
  assetId: string,
): Promise<RepertoireLibraryPiece[]> {
  const result = await get<{ items: RepertoireLibraryPiece[] }>(
    `/admin/repertoire-library/hub-assets/${encodeURIComponent(provider)}/${encodeURIComponent(assetId)}/links`,
  );
  return result.items;
}

export async function attachHubAssetToRepertoire(
  pieceId: string,
  provider: HubAssetProvider,
  assetId: string,
): Promise<RepertoireMaterial> {
  const result = await send<{ resource: RepertoireMaterial }>(
    "POST",
    `/admin/repertoire-library/${encodeURIComponent(pieceId)}/hub-assets`,
    { provider, assetId },
  );
  return result.resource;
}

export async function detachHubAssetFromRepertoire(
  pieceId: string,
  provider: HubAssetProvider,
  assetId: string,
): Promise<void> {
  await send(
    "DELETE",
    `/admin/repertoire-library/${encodeURIComponent(pieceId)}/hub-assets/${encodeURIComponent(provider)}/${encodeURIComponent(assetId)}`,
  );
}

export async function getFamilyRepertoireHubMaterial(
  studentId: string,
  repertoireId: string,
  resourceId: string,
): Promise<GrooveRepertoireMaterialSnapshot> {
  return get<GrooveRepertoireMaterialSnapshot>(
    `/me/students/${encodeURIComponent(studentId)}/repertoire/${encodeURIComponent(repertoireId.replace(/^private:/, ""))}/materials/${encodeURIComponent(resourceId)}/hub`,
  );
}

export function familyRepertoireHubMaterialPath(
  repertoireId: string,
  resourceId: string,
): string {
  return `/progress/repertoire/${encodeURIComponent(repertoireId)}/material/${encodeURIComponent(resourceId)}`;
}


export async function getResourceRepertoireLinks(
  resourceId: string,
): Promise<RepertoireLibraryPiece[]> {
  const result = await get<{ items: RepertoireLibraryPiece[] }>(
    `/admin/repertoire-library/resources/${encodeURIComponent(resourceId)}/links`,
  );
  return result.items;
}

export async function attachResourceToRepertoire(
  pieceId: string,
  resourceId: string,
): Promise<void> {
  await send(
    "POST",
    `/admin/repertoire-library/${encodeURIComponent(pieceId)}/resources/${encodeURIComponent(resourceId)}/link`,
  );
}

export async function detachResourceFromRepertoire(
  pieceId: string,
  resourceId: string,
): Promise<void> {
  await send(
    "DELETE",
    `/admin/repertoire-library/${encodeURIComponent(pieceId)}/resources/${encodeURIComponent(resourceId)}/link`,
  );
}
